import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
    Badge,
    Button,
    Group,
    Modal,
    Select,
    Stack,
    Text,
    Textarea,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useTranslation } from 'react-i18next';

import {
    assignVisit,
    listOrganizationMembers,
    listOrganizations,
    listUsers,
    listVisits,
    listVisitsByOrganization,
    unassignVisit,
} from '../../api/resources';
import { useAuth } from '../../auth/auth-context';
import { DataPanel } from '../../components/DataPanel';
import { EntityRef } from '../../components/EntityRef';
import { PageHeader } from '../../components/PageHeader';
import { PaginatedTable } from '../../components/PaginatedTable';
import { DEFAULT_PAGE_SIZE } from '../../hooks/useListQuery';
import { fieldOfficerUsers } from '../../lib/fieldOfficers';
import { userLabel } from '../../lib/labels';
import { applicantName } from '../../lib/subjectUtils';
import { CASE_TYPE_VALUES, CaseType, VisitStatus } from '../../types/domain';
import type { Visit } from '../../types/api';

/** Dispatch org of a visit: execution org for commissioned cases, else commissioner. */
function dispatchOrgOf(visit: Visit): string | undefined {
    return visit.caseRecord?.executionOrganizationId ?? visit.caseRecord?.organizationId;
}

/**
 * The queue only ever deals with work that is still placeable: unassigned
 * (`available`) or already assigned but not yet acted on. The list endpoint
 * filters by a single status, so each is fetched separately and merged.
 */
const QUEUE_STATUSES = [VisitStatus.Available, VisitStatus.Assigned] as const;

/** Generous page size — the merged set is filtered and paged client-side. */
const FETCH_LIMIT = 500;

function primaryPoi(visit: Visit) {
    return visit.caseRecord?.casePois?.[0]?.poi;
}

function subjectName(visit: Visit): string | undefined {
    return applicantName(visit.caseRecord?.caseSubjects);
}

export function AssignmentQueuePage() {
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const { payload } = useAuth();

    /** Officers always come from the admin's default (first) organization. */
    const defaultOrgId = payload?.organizations?.[0]?.id ?? null;

    const [page, setPage] = useState(1);
    const [orgFilter, setOrgFilter] = useState<string | null>(null);
    const [statusFilter, setStatusFilter] = useState<string | null>(null);
    const [officerFilter, setOfficerFilter] = useState<string | null>(null);
    const [caseTypeFilter, setCaseTypeFilter] = useState<string | null>(CaseType.Monitoring);
    const [selectedIds, setSelectedIds] = useState<string[]>([]);
    const [assignOpen, setAssignOpen] = useState(false);
    const [assignee, setAssignee] = useState<string | null>(null);
    const [reason, setReason] = useState('');

    const orgsQ = useQuery({
        queryKey: ['organizations-all'],
        queryFn: () => listOrganizations({ page: 1, limit: 500 }),
    });
    const usersQ = useQuery({
        queryKey: ['users-field-officers'],
        queryFn: () => listUsers({ page: 1, limit: 500 }),
    });

    const availableQ = useQuery({
        queryKey: ['assignment-queue', VisitStatus.Available, orgFilter, caseTypeFilter],
        queryFn: () => {
            const params = {
                page: 1,
                limit: FETCH_LIMIT,
                status: VisitStatus.Available,
                ...(caseTypeFilter ? { caseType: caseTypeFilter } : {}),
            };
            return orgFilter
                ? listVisitsByOrganization(orgFilter, params)
                : listVisits(params);
        },
    });
    const assignedQ = useQuery({
        queryKey: ['assignment-queue', VisitStatus.Assigned, orgFilter, caseTypeFilter],
        queryFn: () => {
            const params = {
                page: 1,
                limit: FETCH_LIMIT,
                status: VisitStatus.Assigned,
                ...(caseTypeFilter ? { caseType: caseTypeFilter } : {}),
            };
            return orgFilter
                ? listVisitsByOrganization(orgFilter, params)
                : listVisits(params);
        },
    });

    /**
     * Assignable officers belong to the *dispatch* org of the visits in the
     * queue (execution org for commissioned cases, else the commissioning org).
     * When the queue mixes dispatch orgs (or the visit payloads carry no case),
     * the admin's default org scopes the picker as a last resort — the server
     * still rejects agents who are not field-officer members of each visit's
     * dispatch org.
     */
    const poolOrgId = useMemo(() => {
        const rowsAll = [...(availableQ.data?.data ?? []), ...(assignedQ.data?.data ?? [])];
        const orgs = new Set(
            rowsAll.map(dispatchOrgOf).filter((id): id is string => Boolean(id)),
        );
        if (orgs.size === 1) return [...orgs][0];
        return defaultOrgId;
    }, [availableQ.data, assignedQ.data, defaultOrgId]);

    /** Field officers of the dispatch org — the pool of agents that can be assigned. */
    const membersQ = useQuery({
        queryKey: ['organization-members', 'for-org', poolOrgId],
        queryFn: () =>
            listOrganizationMembers({
                page: 1,
                limit: FETCH_LIMIT,
                organizationId: poolOrgId!,
            }),
        enabled: Boolean(poolOrgId),
    });

    /** Every known user, so the officer column resolves names regardless of filters. */
    const officerById = useMemo(
        () => new Map((usersQ.data?.data ?? []).map((u) => [u.id, u])),
        [usersQ.data],
    );

    /**
     * Selectable officers are the dispatch org's field officers, deduped.
     * The org filter scopes the visit queue, not the dispatch semantics.
     */
    const fieldOfficers = useMemo(
        () => fieldOfficerUsers(membersQ.data?.data ?? []),
        [membersQ.data],
    );

    /** Merge both statuses, apply the client-side filters, then sort by due date. */
    const rows = useMemo(() => {
        const merged = [...(availableQ.data?.data ?? []), ...(assignedQ.data?.data ?? [])];
        return merged
            .filter((v) => (statusFilter ? v.status === statusFilter : true))
            .filter((v) => (officerFilter ? v.fieldOfficerId === officerFilter : true))
            .sort((a, b) => {
                const aDue = a.caseRecord?.dueAt ?? '';
                const bDue = b.caseRecord?.dueAt ?? '';
                if (!aDue && !bDue) return 0;
                if (!aDue) return 1;
                if (!bDue) return -1;
                return aDue.localeCompare(bDue);
            });
    }, [availableQ.data, assignedQ.data, statusFilter, officerFilter]);

    const pageRows = rows.slice((page - 1) * DEFAULT_PAGE_SIZE, page * DEFAULT_PAGE_SIZE);

    const assignMutation = useMutation({
        mutationFn: async () => {
            const trimmedReason = reason.trim();
            await Promise.all(
                selectedIds.map((visitId) =>
                    assignVisit(visitId, {
                        assignedAgentId: assignee!,
                        ...(trimmedReason ? { reason: trimmedReason } : {}),
                    }),
                ),
            );
        },
        onSuccess: () => {
            notifications.show({
                color: 'green',
                message: t('features.assignmentQueue.assigned', { count: selectedIds.length }),
            });
            setAssignOpen(false);
            setAssignee(null);
            setReason('');
            setSelectedIds([]);
            void queryClient.invalidateQueries({ queryKey: ['assignment-queue'] });
        },
        onError: (e: Error) => notifications.show({ color: 'red', message: e.message }),
    });

    const unassignMutation = useMutation({
        mutationFn: (visitId: string) => unassignVisit(visitId),
        onSuccess: () => {
            notifications.show({ color: 'green', message: t('features.visits.unassigned') });
            void queryClient.invalidateQueries({ queryKey: ['assignment-queue'] });
            void queryClient.invalidateQueries({ queryKey: ['visits'] });
        },
        onError: () =>
            notifications.show({ color: 'red', message: t('features.visits.couldNotUnassign') }),
    });

    const resetPage = () => setPage(1);
    const isLoading = availableQ.isLoading || assignedQ.isLoading;
    const empty = t('common.empty');

    return (
        <Stack gap="lg">
            <PageHeader
                title={t('features.assignmentQueue.title')}
                description={t('features.assignmentQueue.description')}
            />

            <DataPanel>
                <Group grow align="flex-end">
                    <Select
                        label={t('fields.organization')}
                        placeholder={t('common.all')}
                        clearable
                        searchable
                        data={(orgsQ.data?.data ?? []).map((o) => ({
                            value: o.id,
                            label: o.name,
                        }))}
                        value={orgFilter}
                        onChange={(v) => {
                            setOrgFilter(v);
                            resetPage();
                        }}
                    />
                    <Select
                        label={t('fields.status')}
                        placeholder={t('common.all')}
                        clearable
                        data={QUEUE_STATUSES.map((status) => ({
                            value: status,
                            label: t(`visitStatuses.${status}`, { defaultValue: status }),
                        }))}
                        value={statusFilter}
                        onChange={(v) => {
                            setStatusFilter(v);
                            resetPage();
                        }}
                    />
                    <Select
                        label={t('fields.fieldOfficer')}
                        placeholder={t('common.all')}
                        clearable
                        searchable
                        data={fieldOfficers.map((u) => ({
                            value: u.id,
                            label: userLabel(u),
                        }))}
                        value={officerFilter}
                        onChange={(v) => {
                            setOfficerFilter(v);
                            resetPage();
                        }}
                    />
                    <Select
                        label={t('fields.caseType')}
                        placeholder={t('common.all')}
                        clearable
                        data={CASE_TYPE_VALUES.map((caseType) => ({
                            value: caseType,
                            label: t(`caseTypes.${caseType}`, { defaultValue: caseType }),
                        }))}
                        value={caseTypeFilter}
                        onChange={(v) => {
                            setCaseTypeFilter(v);
                            resetPage();
                        }}
                    />
                </Group>
            </DataPanel>

            {selectedIds.length > 0 ? (
                <Group
                    justify="space-between"
                    p="sm"
                    px="md"
                    style={{
                        borderRadius: 'var(--mantine-radius-lg)',
                        backgroundColor: 'var(--mantine-color-default-hover)',
                    }}
                >
                    <Text size="sm" fw={600}>
                        {t('features.assignmentQueue.selectedCount', {
                            count: selectedIds.length,
                        })}
                    </Text>
                    <Group gap="sm">
                        <Button variant="subtle" size="xs" onClick={() => setSelectedIds([])}>
                            {t('common.actions.clearSelection')}
                        </Button>
                        <Button size="xs" onClick={() => setAssignOpen(true)}>
                            {t('features.assignmentQueue.assignAction')}
                        </Button>
                    </Group>
                </Group>
            ) : null}

            <PaginatedTable
                selection={{ selectedIds, onChange: setSelectedIds }}
                rowKey={(r: Visit) => r.id}
                columns={[
                    {
                        key: 'case',
                        header: t('fields.case'),
                        render: (r) => (
                            <EntityRef
                                title={
                                    r.caseRecord?.externalReferenceId ??
                                    (r.caseRecord?.caseType
                                        ? t(`caseTypes.${r.caseRecord.caseType}`, {
                                              defaultValue: r.caseRecord.caseType,
                                          })
                                        : t('entities.case'))
                                }
                                id={r.caseId}
                                href={`/cases/${r.caseId}`}
                            />
                        ),
                    },
                    {
                        key: 'subject',
                        header: t('fields.subject'),
                        render: (r) => subjectName(r) ?? empty,
                    },
                    {
                        key: 'caseType',
                        header: t('fields.caseType'),
                        render: (r) =>
                            r.caseRecord?.caseType ? (
                                <Badge variant="light" size="sm">
                                    {t(`caseTypes.${r.caseRecord.caseType}`, {
                                        defaultValue: r.caseRecord.caseType,
                                    })}
                                </Badge>
                            ) : (
                                empty
                            ),
                    },
                    {
                        key: 'address',
                        header: t('fields.address'),
                        render: (r) => primaryPoi(r)?.claimedAddress ?? empty,
                    },
                    {
                        key: 'state',
                        header: t('features.assignmentQueue.area'),
                        render: (r) => {
                            const poi = primaryPoi(r);
                            return (
                                [poi?.claimedCity, poi?.claimedState]
                                    .filter(Boolean)
                                    .join(', ') || empty
                            );
                        },
                    },
                    {
                        key: 'dueAt',
                        header: t('fields.dueAt'),
                        render: (r) =>
                            r.caseRecord?.dueAt
                                ? new Date(r.caseRecord.dueAt).toLocaleDateString()
                                : empty,
                    },
                    {
                        key: 'status',
                        header: t('fields.status'),
                        render: (r) =>
                            r.status ? (
                                <Badge
                                    variant="light"
                                    size="sm"
                                    color={r.status === VisitStatus.Available ? 'yellow' : 'blue'}
                                >
                                    {t(`visitStatuses.${r.status}`, { defaultValue: r.status })}
                                </Badge>
                            ) : (
                                empty
                            ),
                    },
                    {
                        key: 'officer',
                        header: t('fields.fieldOfficer'),
                        render: (r) => {
                            const officer = r.fieldOfficerId
                                ? officerById.get(r.fieldOfficerId)
                                : undefined;
                            return officer ? userLabel(officer) : empty;
                        },
                    },
                    {
                        key: 'actions',
                        header: '',
                        render: (r) =>
                            r.status === VisitStatus.Assigned && r.fieldOfficerId ? (
                                <Button
                                    size="compact-xs"
                                    variant="subtle"
                                    color="red"
                                    loading={
                                        unassignMutation.isPending &&
                                        unassignMutation.variables === r.id
                                    }
                                    disabled={unassignMutation.isPending}
                                    onClick={() => unassignMutation.mutate(r.id)}
                                >
                                    {t('common.actions.unassign')}
                                </Button>
                            ) : null,
                    },
                ]}
                rows={pageRows}
                total={rows.length}
                page={page}
                pageSize={DEFAULT_PAGE_SIZE}
                onPageChange={setPage}
                isLoading={isLoading}
                emptyMessage={t('features.assignmentQueue.emptyQueue')}
            />

            <Modal
                opened={assignOpen}
                onClose={() => setAssignOpen(false)}
                title={t('features.assignmentQueue.assignModalTitle')}
            >
                <Stack>
                    <Text size="sm" c="dimmed">
                        {t('features.assignmentQueue.assignHint', { count: selectedIds.length })}
                    </Text>
                    <Select
                        label={t('fields.fieldOfficer')}
                        placeholder={t('features.assignmentQueue.selectOfficer')}
                        required
                        searchable
                        data={fieldOfficers.map((u) => ({ value: u.id, label: userLabel(u) }))}
                        value={assignee}
                        onChange={setAssignee}
                    />
                    <Textarea
                        label={t('fields.reason')}
                        placeholder={t('common.optional')}
                        minRows={2}
                        value={reason}
                        onChange={(e) => setReason(e.currentTarget.value)}
                    />
                    <Group justify="flex-end">
                        <Button variant="default" onClick={() => setAssignOpen(false)}>
                            {t('common.actions.cancel')}
                        </Button>
                        <Button
                            disabled={!assignee}
                            loading={assignMutation.isPending}
                            onClick={() => assignMutation.mutate()}
                        >
                            {t('features.assignmentQueue.assignAction')}
                        </Button>
                    </Group>
                </Stack>
            </Modal>
        </Stack>
    );
}
